import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core'
import { CouponRateHistory } from '../../models/coupon-rate-history.model'
import {
  ColDef,
  GridOptions,
  Module,
  RowDataChangedEvent,
} from '@ag-grid-community/core'
import { Subject } from 'rxjs'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  getFilterOnCellContextMenu,
  heliosClientSideRowConfig,
  heliosDefaultColWidth,
} from '@helios/ag-grid-config'
import { composeFieldPath } from '@northfork/basics'
import { NfColumnTypes } from '@northfork/ag-grid'
import { formatDates } from '@helios/common-api'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { takeUntil } from 'rxjs/operators'
import { TableLayoutComponent } from '@northfork/ag-grid-angular'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'

const couponRateHistoryPath = composeFieldPath([] as CouponRateHistory[])

@Component({
  selector: 'hs-coupon-rate-history-grid[data]',
  templateUrl: './coupon-rate-history-grid.component.html',
})
export class CouponRateHistoryGridComponent implements OnDestroy {
  @ViewChild('table')
  readonly table?: TableLayoutComponent<CouponRateHistory>

  @Input() data: CouponRateHistory[] | null = null

  readonly gridModules: Module[] = [FiltersToolPanelModule]
  readonly gridOptions: GridOptions = this.createGridOptions()
  readonly destroySubject$ = new Subject<void>()

  constructor(
    readonly changeDetector: ChangeDetectorRef,
    readonly preferences: UserPreferencesService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  createGridOptions(): GridOptions {
    return {
      columnDefs: this.createColumnDefs(),
      getContextMenuItems: params => getFilterOnCellContextMenu(params),
      ...heliosClientSideRowConfig,
      onRowDataChanged: event => this.onRowDataChanged(event),
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onGridReady: params => {
        enableHeliosCopyBehavior(params.api)
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
      },
    }
  }

  // @internal
  createColumnDefs(): ColDef[] {
    return [
      {
        field: couponRateHistoryPath('securityId'),
        headerName: 'Security ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: couponRateHistoryPath('accountId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: couponRateHistoryPath('date'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: couponRateHistoryPath('couponRate'),
        type: NfColumnTypes.Identifier,
      },
    ]
  }

  onRowDataChanged(event: RowDataChangedEvent) {
    if (this.data != null) {
      return
    }
    event.api.showLoadingOverlay()
  }
}
